import type { SkinRarity, WearLevel } from './weapon-skin-types';
import type { StickerInstance } from './sticker-types';

/** Status of a market listing */
export type ListingStatus = 'active' | 'sold' | 'cancelled' | 'expired';

/** A marketplace listing */
export interface MarketListing {
  /** Unique listing ID (UUID) */
  listingId: string;
  /** Seller's account ID */
  sellerAccountId: string;
  /** Seller's display name */
  sellerName: string;
  /** Skin instance ID being sold */
  instanceId: string;
  /** Skin definition ID */
  skinId: number;
  /** Skin display name */
  skinName: string;
  /** Weapon this skin is for */
  weaponId: string;
  /** Wear float of the listed item */
  wearFloat: number;
  /** Pattern seed (for pattern-paint skins) */
  patternSeed: number;
  /** Whether the item is StatTrak */
  isStatTrak: boolean;
  /** StatTrak kill count (-1 if not StatTrak) */
  statTrakKills: number;
  /** Has name tag */
  nameTag: string;
  /** Rarity tier */
  rarity: SkinRarity;
  /** Asking price in in-game currency */
  price: number;
  /** When listed (timestamp ms) */
  listedAt: number;
  /** Current status */
  status: ListingStatus;
  /** Applied stickers, if this runtime has that data available */
  stickers?: (StickerInstance | null)[];
  /**
   * Existing trade-lock on the listed instance, in epoch ms. 0 / undefined
   * means the instance is not currently trade-locked. The 7-day lock that
   * applies to the buyer after purchase is communicated via
   * `tradeLockDurationMs` (the duration), not via this field.
   */
  tradeLockUntilMs?: number;
  /**
   * Duration of the trade-lock that will apply to the buyer once they
   * purchase this listing. Server defaults this to 7 days; clients use it
   * to render a "7-day trade lock" warning before confirming a purchase.
   */
  tradeLockDurationMs?: number;
}

/** Create listing request */
export interface CreateListingRequest {
  instanceId: string;
  skinId: number;
  skinName: string;
  weaponId: string;
  rarity: SkinRarity;
  wearFloat: number;
  patternSeed: number;
  isStatTrak: boolean;
  statTrakKills: number;
  nameTag: string;
  stickers?: (StickerInstance | null)[];
  price: number;
}

/** Buy listing request */
export interface BuyListingRequest {
  listingId: string;
}

/** Market search filters */
export interface MarketSearchFilters {
  /** Filter by weapon type */
  weaponId?: string;
  /** Filter by skin name (partial match) */
  skinName?: string;
  /** Filter by rarity tier */
  rarity?: SkinRarity;
  /** Filter by wear level */
  wearLevel?: WearLevel;
  /** Min wear float */
  minWear?: number;
  /** Max wear float */
  maxWear?: number;
  /** Min price */
  minPrice?: number;
  /** Max price */
  maxPrice?: number;
  /** Only StatTrak items */
  statTrakOnly?: boolean;
  /** Sort field */
  sortBy?: 'price-asc' | 'price-desc' | 'wear-asc' | 'wear-desc' | 'listed-desc' | 'listed-asc';
  /** Pagination offset */
  offset?: number;
  /** Results per page (max 50) */
  limit?: number;
}

/** Market search response */
export interface MarketSearchResponse {
  listings: MarketListing[];
  totalCount: number;
  offset: number;
  limit: number;
}

/** Purchase result */
export interface PurchaseResult {
  success: boolean;
  listingId: string;
  instanceId?: string;
  error?: string;
  /** Buyer's new balance after purchase */
  newBalance?: number;
  /** Trade-lock expiry on the bought instance (epoch ms). */
  tradeLockUntilMs?: number;
}

/** Cancel listing result */
export interface CancelListingResult {
  success: boolean;
  listingId: string;
  instanceId?: string;
  error?: string;
}

/** Default trade-lock duration applied on purchase (7 days). */
export const DEFAULT_TRADE_LOCK_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

/** Price history entry for market analytics */
export interface PriceHistoryEntry {
  skinId: number;
  weaponId: string;
  averagePrice: number;
  minPrice: number;
  maxPrice: number;
  salesCount: number;
  /** Period this entry covers (e.g., "2026-03-20") */
  period: string;
}

/** Market statistics for a skin */
export interface SkinMarketStats {
  skinId: number;
  weaponId: string;
  activeListings: number;
  lowestPrice: number;
  medianPrice: number;
  recentSales: number;
  priceHistory: PriceHistoryEntry[];
}

/** Maximum listing price */
export const MAX_LISTING_PRICE = 999_999;

/** Minimum listing price */
export const MIN_LISTING_PRICE = 1;

/** Market listing expiration time in ms (30 days) */
export const LISTING_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/** Maximum active listings per player */
export const MAX_ACTIVE_LISTINGS = 20;

/** Market fee percentage (5% like Steam) */
export const MARKET_FEE_PERCENT = 5;

/** Results per page default */
export const DEFAULT_PAGE_SIZE = 20;

/** Maximum results per page */
export const MAX_PAGE_SIZE = 50;

/** Calculate the market fee for a given price */
export function calculateMarketFee(price: number): number {
  return Math.max(1, Math.floor(price * MARKET_FEE_PERCENT / 100));
}

/** Calculate seller proceeds after fee */
export function calculateSellerProceeds(price: number): number {
  return price - calculateMarketFee(price);
}

/** Validate listing price */
export function isValidListingPrice(price: number): boolean {
  return Number.isInteger(price) && price >= MIN_LISTING_PRICE && price <= MAX_LISTING_PRICE;
}
